import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Eye } from "lucide-react";
import api, { apiError, BULAN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const STATUS_BADGE = {
  pending: { label: "Menunggu", cls: "bg-amber-50 text-amber-700 hover:bg-amber-50" },
  approved: { label: "Disetujui", cls: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" },
  rejected: { label: "Ditolak", cls: "bg-rose-50 text-rose-700 hover:bg-rose-50" },
};

const kindLabel = (p) =>
  !p ? ""
  : p.kind === "spp" ? `SPP ${BULAN[p.month]} ${p.year}`
  : p.kind === "bill" ? p.bill_title
  : p.kind === "setor" ? "Setor Tabungan" : "Tarik Tabungan";

function formatWaktu(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function PaymentConfirmations() {
  const { user, schoolId } = useAuth();
  const canAct = ["super_admin", "bendahara"].includes(user.role);
  const [list, setList] = useState(null);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const load = () => {
    setNeedsSchool(false);
    api.get(`/payments/online${statusFilter !== "semua" ? `?status=${statusFilter}` : ""}`)
      .then((r) => setList(r.data))
      .catch((e) => {
        if (e.response?.status === 400) { setNeedsSchool(true); setList([]); }
        else { setList([]); toast.error(apiError(e)); }
      });
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) { setNeedsSchool(true); setList([]); return; }
    load();
  }, [schoolId, statusFilter]);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const r = await api.post(`/payments/online/${approveTarget.id}/approve`);
      toast.success(`Pembayaran disetujui — ${r.data.status}`);
      setApproveTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await api.post(`/payments/online/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success("Pengajuan ditolak");
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const proofUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.proof_path}?auth=${localStorage.getItem("sb_token")}`;

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="confirmations-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="confirmations-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Konfirmasi Pembayaran Online</h1>
        <p className="mt-1 text-sm text-slate-500">Periksa bukti transfer/QRIS dari orang tua, lalu setujui atau tolak.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 max-w-xs">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="confirmations-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending" data-testid="filter-pending">Menunggu Konfirmasi</SelectItem>
              <SelectItem value="approved" data-testid="filter-approved">Disetujui</SelectItem>
              <SelectItem value="rejected" data-testid="filter-rejected">Ditolak</SelectItem>
              <SelectItem value="semua" data-testid="filter-semua">Semua</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="confirmations-table">
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Siswa</TableHead>
                <TableHead>Pembayaran</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead>Bukti</TableHead>
                <TableHead>Status</TableHead>
                {canAct && <TableHead className="text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {list === null && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {list !== null && list.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">Tidak ada pengajuan pada status ini.</TableCell></TableRow>
              )}
              {(list || []).map((p) => (
                <TableRow key={p.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`confirmation-row-${p.id}`}>
                  <TableCell className="whitespace-nowrap text-sm text-slate-600">{formatWaktu(p.created_at)}</TableCell>
                  <TableCell className="text-sm text-slate-800">
                    <span className="font-medium">{p.student_name}</span>
                    <span className="block text-xs text-slate-400">{p.class_name} · oleh {p.parent_name}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{kindLabel(p)}</TableCell>
                  <TableCell className="text-right text-sm font-medium text-slate-800">Rp {p.amount.toLocaleString("id-ID")}</TableCell>
                  <TableCell>
                    {p.proof_path ? (
                      <a href={proofUrl(p)} target="_blank" rel="noreferrer" data-testid={`proof-link-${p.id}`}
                         className="flex items-center gap-1 text-sm font-medium text-[#0060AF] hover:underline">
                        <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> Lihat
                      </a>
                    ) : (
                      <span className="text-sm text-slate-300" data-testid={`proof-none-${p.id}`}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-full ${STATUS_BADGE[p.status]?.cls || ""}`} data-testid={`confirmation-status-${p.id}`}>
                      {STATUS_BADGE[p.status]?.label || p.status}
                    </Badge>
                    {p.status === "rejected" && p.reject_reason && <span className="block text-xs text-rose-500">{p.reject_reason}</span>}
                  </TableCell>
                  {canAct && (
                    <TableCell className="text-right">
                      {p.status === "pending" && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" onClick={() => setApproveTarget(p)} data-testid={`approve-payment-${p.id}`}
                                  className="bg-emerald-600 text-white hover:bg-emerald-700">
                            Setujui
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setRejectTarget(p); setRejectReason(""); }} data-testid={`reject-payment-${p.id}`}
                                  className="border-rose-300 text-rose-600 hover:bg-rose-50">
                            Tolak
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <ConfirmDialog
        open={!!approveTarget}
        onOpenChange={() => setApproveTarget(null)}
        title="Setujui Pembayaran"
        description={`Setujui ${kindLabel(approveTarget)} dari ${approveTarget?.student_name} sebesar Rp ${approveTarget?.amount.toLocaleString("id-ID")}? ${approveTarget?.kind === "setor" ? "Saldo tabungan siswa langsung bertambah." : approveTarget?.kind === "tarik" ? "Saldo tabungan siswa langsung terpotong." : "Pembayaran langsung tercatat di pembukuan."}`}
        onConfirm={handleApprove}
        testId="confirm-approve-payment"
      />

      <Dialog open={!!rejectTarget} onOpenChange={() => setRejectTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Tolak Pengajuan — {rejectTarget?.student_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReject} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Alasan Penolakan (dikirim ke WA wali)</Label>
              <Textarea id="reject-reason" data-testid="reject-reason-input" value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)} rows={3}
                        placeholder="Contoh: nominal transfer tidak sesuai / bukti tidak terbaca" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRejectTarget(null)} data-testid="reject-cancel">Batal</Button>
              <Button type="submit" disabled={processing} data-testid="reject-submit" className="bg-rose-600 text-white hover:bg-rose-700">
                {processing ? "Memproses..." : "Tolak Pengajuan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
